import type { AuthSession } from "./types";

const SESSION_KEY = "cls_word_addin_session";
/** Office Dialog mở cửa sổ mới — sessionStorage không chia sẻ; dùng localStorage làm fallback. */
const SESSION_SHARED_KEY = "cls_word_addin_session_shared";

let memorySession: AuthSession | null = null;
const listeners = new Set<(session: AuthSession | null) => void>();

function notifyListeners(session: AuthSession | null): void {
  for (const listener of listeners) {
    listener(session);
  }
}

export function subscribeSession(listener: (session: AuthSession | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function readStoredSessionRaw(): string | null {
  return sessionStorage.getItem(SESSION_KEY) ?? localStorage.getItem(SESSION_SHARED_KEY);
}

function writeStoredSession(raw: string): void {
  sessionStorage.setItem(SESSION_KEY, raw);
  try {
    localStorage.setItem(SESSION_SHARED_KEY, raw);
  } catch {
    // ignore — private mode / quota
  }
}

export function saveSession(session: AuthSession): void {
  memorySession = session;
  writeStoredSession(JSON.stringify(session));
  notifyListeners(session);
}

export function getSession(): AuthSession | null {
  if (memorySession && memorySession.expiresAt > Date.now()) {
    return memorySession;
  }

  const raw = readStoredSessionRaw();
  if (!raw) {
    memorySession = null;
    return null;
  }

  try {
    const session = JSON.parse(raw) as AuthSession;
    if (!session.accessToken || session.expiresAt <= Date.now()) {
      clearSession();
      return null;
    }
    memorySession = session;
    return session;
  } catch {
    clearSession();
    return null;
  }
}

export function clearSession(): void {
  const hadSession =
    memorySession !== null ||
    sessionStorage.getItem(SESSION_KEY) !== null ||
    localStorage.getItem(SESSION_SHARED_KEY) !== null;
  memorySession = null;
  sessionStorage.removeItem(SESSION_KEY);
  localStorage.removeItem(SESSION_SHARED_KEY);
  if (hadSession) {
    notifyListeners(null);
  }
}
